import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiFileText, FiLoader, FiAlertCircle, FiInbox, FiClock, FiEye, FiType, FiDatabase,
} from 'react-icons/fi';
import { documentLibraryAPI } from '../../services/api';
import { formatDateMedium } from '../../utils/dateFormat';

/**
 * AdvancedSearchPanel — server-side advanced search results for the Document
 * Management screen. One query runs against contract metadata, document full
 * text and OCR text at once; every returned row carries its match method, so
 * the filter chips narrow client-side without refetching. Everything shown
 * here is already permission-filtered by the backend.
 */

const MIN_QUERY_LENGTH = 2;
const TYPE_FILTERS = ['all', 'metadata', 'fulltext', 'ocr'];

const MATCH_ICON = {
  metadata: <FiDatabase />,
  fulltext: <FiType />,
  ocr: <FiEye />,
};

export default function AdvancedSearchPanel({ query, onOpenResult }) {
  const { t } = useTranslation();
  const [typeFilter, setTypeFilter] = useState('all');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const q = (query || '').trim();
  const active = q.length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    // Any query change invalidates whatever request is in flight — a stale
    // response must never land as the settled state for the new query.
    abortRef.current?.abort();
    if (!active) {
      setResults([]); setTotal(0); setError(null); setLoading(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setError(null);
      documentLibraryAPI.advancedSearch(q, null, controller.signal)
        .then((res) => {
          setResults(res.data.results || []);
          setTotal(res.data.total || 0);
          setTruncated(!!res.data.truncated);
          setLoading(false);
        })
        .catch((err) => {
          if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
          setError(err.response?.data?.error || t('documentLibrary.advancedSearch.failed'));
          setLoading(false);
        });
    }, 350);
    return () => { clearTimeout(timer); };
  }, [q, active, t]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const visible = useMemo(
    () => (typeFilter === 'all' ? results : results.filter((r) => r.matchType === typeFilter)),
    [results, typeFilter]
  );

  if (!active) return null;

  const docTypeLabel = (r) => {
    if (r.kind === 'contract') return t('documentLibrary.advancedSearch.kind.contract');
    if (r.documentCategory === 'library') return t('documentLibrary.advancedSearch.kind.libraryFile');
    if (r.documentCategory) {
      return t(`documentLibrary.systemFolders.${r.documentCategory}`, { defaultValue: r.documentCategory });
    }
    return t('documentLibrary.advancedSearch.kind.document');
  };

  const versionBadge = (r) => {
    const badges = [];
    if (r.isOldContractVersion) {
      badges.push(
        <span key="oldc" className="doclib-adv__old" title={t('documentLibrary.advancedSearch.oldContractVersionHint')}>
          <FiClock /> {t('documentLibrary.advancedSearch.oldContractVersion', {
            type: t(`documentLibrary.advancedSearch.versionType.${r.contractVersionType || 'original'}`),
            version: r.majorVersion,
          })}
        </span>
      );
    } else if (r.contractVersionType && r.contractVersionType !== 'original') {
      badges.push(
        <span key="vt" className="doclib-adv__vtype">
          {t(`documentLibrary.advancedSearch.versionType.${r.contractVersionType}`)}
        </span>
      );
    }
    if (r.isOldDocVersion) {
      badges.push(
        <span key="oldd" className="doclib-adv__old" title={t('documentLibrary.advancedSearch.oldDocVersionHint')}>
          <FiClock /> {t('documentLibrary.advancedSearch.oldDocVersion', { version: r.versionNumber })}
        </span>
      );
    }
    return badges;
  };

  const showEmpty = !loading && !error && visible.length === 0;

  return (
    <section className="doclib-adv" aria-label={t('documentLibrary.advancedSearch.title')}>
      <div className="doclib-adv__head">
        <h3>{t('documentLibrary.advancedSearch.title')}</h3>
        <div className="doclib-adv__chips" role="tablist">
          {TYPE_FILTERS.map((tf) => (
            <button
              key={tf}
              role="tab"
              aria-selected={typeFilter === tf}
              className={`doclib-adv__chip ${typeFilter === tf ? 'doclib-adv__chip--active' : ''}`}
              onClick={() => setTypeFilter(tf)}
            >
              {t(`documentLibrary.advancedSearch.filters.${tf}`)}
            </button>
          ))}
        </div>
        {loading ? (
          <span className="doclib-adv__count"><FiLoader className="doclib-spin" /></span>
        ) : !error && (
          <span className="doclib-adv__count">
            {truncated
              ? t('documentLibrary.advancedSearch.countTruncated', { shown: results.length, total })
              : t('documentLibrary.advancedSearch.count', { count: total })}
          </span>
        )}
      </div>

      {error && (
        <div className="doclib-adv__state doclib-adv__state--error"><FiAlertCircle /> {error}</div>
      )}
      {showEmpty && (
        <div className="doclib-adv__state">
          {loading ? <FiLoader className="doclib-spin" /> : <FiInbox />}{' '}
          {loading
            ? t('documentLibrary.advancedSearch.searching')
            : t('documentLibrary.advancedSearch.noResults')}
        </div>
      )}

      {!error && visible.length > 0 && (
        <div className="doclib-adv__tablewrap">
          <table className="doclib-adv__table">
            <thead>
              <tr>
                <th>{t('documentLibrary.advancedSearch.columns.contractNo')}</th>
                <th>{t('documentLibrary.advancedSearch.columns.contractName')}</th>
                <th>{t('documentLibrary.advancedSearch.columns.documentName')}</th>
                <th>{t('documentLibrary.advancedSearch.columns.documentType')}</th>
                <th>{t('documentLibrary.advancedSearch.columns.counterparty')}</th>
                <th>{t('documentLibrary.advancedSearch.columns.matchType')}</th>
                <th>{t('documentLibrary.advancedSearch.columns.updatedAt')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr
                  key={`${r.kind}-${r.documentId || r.contractId || i}-${r.matchType}`}
                  onClick={() => onOpenResult(r)}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOpenResult(r); }}
                >
                  <td className="doclib-adv__mono">{r.contractNumber || '—'}</td>
                  <td>
                    <span className="doclib-adv__name">{r.contractName || r.folderName || '—'}</span>
                    {versionBadge(r)}
                  </td>
                  <td>
                    {r.documentTitle ? (
                      <span className="doclib-adv__doc"><FiFileText /> {r.documentTitle}</span>
                    ) : '—'}
                    {r.snippet && (
                      <div className="doclib-adv__snippet" title={r.snippet}>{r.snippet}</div>
                    )}
                  </td>
                  <td>{docTypeLabel(r)}</td>
                  <td>{r.counterpartyName || '—'}</td>
                  <td>
                    <span className={`doclib-adv__match doclib-adv__match--${r.matchType}`}>
                      {MATCH_ICON[r.matchType]} {t(`documentLibrary.advancedSearch.matchTypes.${r.matchType}`)}
                    </span>
                  </td>
                  <td className="doclib-adv__date">{formatDateMedium(r.updatedAt, '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
